import { useMemo } from 'react';

import {
  useIsInsightsCampaignEnabled,
  useIsInsightsLeadersMyInsightsNavigationEnabled,
  useIsInsightsLeadersOrgNavigationEnabled,
  useIsInsightsQnAMyInsightsEnabled,
  useIsInsightsQnAOrgEnabled,
  useIsPremiumCommunityInsightsV2Enabled,
  useIsQuestionsOnlyMode,
} from '../../featureChecks';
import type { UseMainUIFeatureSettings } from '../../types';

/**
 * @deprecated `@ms/yammer-web-ui-features` is a monolithic package which adds bloat from all feature access settings
 * to a single bootstrap API call (FeatureAccessSettingsClients) and JS assets. DO NOT add new code to this package.
 * Your team is responsible for migrating fields and code out of this package as you move your features to Relay, where
 * your feature should request the necessary `viewerCanXX` fields in its own colocated fragment. Note that we may also
 * need to design a new scalable permissions-check solution for features which require additional logic to check access.
 */
export const useInsightsNavigationSettings: UseMainUIFeatureSettings<'insightsNavigation'> = (options) => {
  const insightsNavigationOverrides = options?.overrides?.insightsNavigation;

  const isQuestionsOnlyMode = useIsQuestionsOnlyMode();

  const campaign = useIsInsightsCampaignEnabled();
  const premiumCommunityInsightsV2 = useIsPremiumCommunityInsightsV2Enabled();
  const leadersMyInsights = useIsInsightsLeadersMyInsightsNavigationEnabled();
  const leadersOrg = useIsInsightsLeadersOrgNavigationEnabled();
  const qnaMyInsights = useIsInsightsQnAMyInsightsEnabled();
  const qnaOrg = useIsInsightsQnAOrgEnabled();

  return useMemo(() => {
    if (isQuestionsOnlyMode) {
      return {
        campaign: false,
        premiumCommunityInsightsV2: false,
        leadersMyInsights: false,
        leadersOrg: false,
        qnaMyInsights,
        qnaOrg,
        ...insightsNavigationOverrides,
      };
    }

    return {
      campaign,
      premiumCommunityInsightsV2,
      leadersMyInsights,
      leadersOrg,
      qnaMyInsights,
      qnaOrg,
      ...insightsNavigationOverrides,
    };
  }, [
    campaign,
    premiumCommunityInsightsV2,
    insightsNavigationOverrides,
    isQuestionsOnlyMode,
    leadersMyInsights,
    leadersOrg,
    qnaMyInsights,
    qnaOrg,
  ]);
};
